import { useState, useEffect, useCallback } from 'react';
import {
  aiIntelligenceService,
  DevelopmentPlan,
  PlanEditRequest,
} from '../services/aiIntelligenceService';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

interface UsePlanDisplayReturn {
  plan: DevelopmentPlan | null;
  loadingState: LoadingState;
  error: string | null;
  editingSection: string | null;
  isSaving: boolean;
  loadPlan: (planId: string) => void;
  startEditing: (sectionKey: string) => void;
  cancelEditing: () => void;
  saveEdit: (edit: PlanEditRequest) => Promise<boolean>;
}

export function usePlanDisplay(initialPlanId?: string): UsePlanDisplayReturn {
  const [plan, setPlan] = useState<DevelopmentPlan | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadPlan = useCallback(async (planId: string) => {
    setLoadingState('loading');
    setError(null);
    try {
      const fetchedPlan = await aiIntelligenceService.getPlan(planId);
      setPlan(fetchedPlan);
      setLoadingState('loaded');
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || 'Failed to load development plan.'
      );
      setLoadingState('error');
    }
  }, []);

  useEffect(() => {
    if (initialPlanId) {
      loadPlan(initialPlanId);
    }
  }, [initialPlanId, loadPlan]);

  const startEditing = useCallback((sectionKey: string) => {
    setEditingSection(sectionKey);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingSection(null);
  }, []);

  const saveEdit = useCallback(
    async (edit: PlanEditRequest): Promise<boolean> => {
      if (!plan) return false;
      setIsSaving(true);
      try {
        await aiIntelligenceService.editPlan(plan.planId, edit);
        // Reload the plan to get the updated version with edit history
        await loadPlan(plan.planId);
        setEditingSection(null);
        setIsSaving(false);
        return true;
      } catch (err: any) {
        setIsSaving(false);
        return false;
      }
    },
    [plan, loadPlan]
  );

  return {
    plan,
    loadingState,
    error,
    editingSection,
    isSaving,
    loadPlan,
    startEditing,
    cancelEditing,
    saveEdit,
  };
}
